/**
 * A filter's operator, as a switch across the top of its value panel.
 *
 * For a chip whose question is a choice between two or three words — a lookup's
 * "is" / "is not" — a menu on the chip was a second control to reach the one
 * that mattered. Sat above the search box it is in the panel the reader has
 * already opened, and flipping it keeps whatever they picked.
 *
 * Both filter controls draw it: the app's entity picker, through `Picker`'s
 * `header` slot, and the report builder's own list for a vocabulary the model
 * states. PRESENTATION ONLY — the words and what they mean are the caller's.
 *
 * A labelled group of `aria-pressed` buttons rather than a radiogroup, like the
 * dashboard's segmented toggles: the options are states of one control, not
 * form values, and a radiogroup's roving tabindex would take Tab away from the
 * search box below.
 */

import { cn } from '../lib/utils';

export interface FilterOperatorSwitchProps<T extends string> {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  /** Accessible name for the group — say which filter. */
  label: string;
  className?: string;
}

export function FilterOperatorSwitch<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: FilterOperatorSwitchProps<T>) {
  return (
    <fieldset
      aria-label={label}
      className={cn('flex w-full items-center gap-0.5 rounded-md bg-muted p-0.5', className)}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
            className={cn(
              'flex-1 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </fieldset>
  );
}
